/*
 * This is a THEME-DATA module, not a component module: it exports a small set of pure helpers + a
 * category descriptor for the equipped-cosmetics look. No JSX/components live here, so we opt the whole
 * file out of the react-refresh component-only rule (Fast Refresh granularity is irrelevant for data).
 */
/* eslint-disable react-refresh/only-export-components */
import type { EquippedCosmeticsResponse } from '@kidshome/api-contract';
import { lookItem, type EquippedLook } from '@kidshome/hero-content/art';

// The equipped-look map + accessor have ONE definition in the hero-content package (ADR-0110); re-export them
// here so every web surface keeps importing them from `@/entities/cosmetics` unchanged.
export { lookItem, type EquippedLook };

/**
 * The EQUIPPED-LOOK helpers (ADR-0098/0099).
 *
 * Equipped cosmetics dress the HERO (the single dressed-up character): each hero's worn + card art lives
 * WITH its hero in `@kidshome/hero-content` (ADR-0100) and is rendered by the hero plugin. The MEMBER
 * AVATAR is a plain identity face — it carries NO cosmetic overlay (the former legacy member-avatar overlay
 * layer was removed, so a cosmetic never renders inconsistently across the avatar and the hero).
 *
 * What remains here is hero-agnostic + pure: the `EquippedLook` map helpers shared by every surface that
 * reads a worn look (the hero figure, the wardrobe previews, memo keys). Heroes are SKINS now (ADR-0110), so
 * wear-PLACES are declared per hero (royal: `royal_look` + `royal_scene`) — there is no fixed category set.
 */

/**
 * A stable, order-independent signature of a worn look — changes exactly when the kid equips/removes
 * something in any place. Shared by every surface that reacts to a look change (the wardrobe equip pop,
 * memo keys) so the "did the look change?" test is computed one way everywhere.
 */
export function lookSignature(look?: EquippedLook): string {
  if (!look) return '';
  return Object.keys(look)
    .sort()
    .map((place) => `${place}:${look[place] ?? ''}`)
    .join('|');
}

/**
 * Narrow an equipped-cosmetics response to a place → item-id map. Tolerates a missing/loading response so
 * the visuals can always fall back to the default (empty) look.
 */
export function equippedLook(data: EquippedCosmeticsResponse | null | undefined): EquippedLook {
  const out: Record<string, string | null> = {};
  for (const e of data?.equipped ?? []) {
    out[e.place] = e.itemId;
  }
  return out;
}
